import { TimesheetsUtilitiesError } from './errorMessages'
import { convertFirestoreTimestampToDate,
  convertDateWithTimeToFirestoreTimestamp,
  convertDateStringToFirestoreTimestamp
 } from './FirestoreUtilities'

export const performDefaultSortForTimesheetRefs = (timesheetRefs) => {
  return timesheetRefs.sort((timesheetRefA, timesheetRefB) => {
    return timesheetRefA.data().timeIn < timesheetRefB.data().timeIn ? -1 : 1
  })  
}

export default class TimesheetUtilities {

  constructor(firebase, db, timesheetRefs) {
    this.firebase = firebase
    this.db = db
    this.timesheetRefs = timesheetRefs
  }

  getDateTimeProperties = () => {
    return [
      'timeIn',
      'timeOut',
    ]
  }

  convertTimeentryForTable = (timeentryRef) => {
    var dateProperties = this.getDateTimeProperties()
    const convertedTimeentry = timeentryRef.data()
    dateProperties.forEach(property => {
      const tempTimestamp = convertedTimeentry[property]
      convertedTimeentry[property] = convertFirestoreTimestampToDate(this.firebase, tempTimestamp)
    })
    return {ref: timeentryRef.id, ...convertedTimeentry}
  }

  fetchRefsForAll = () => {
    return this.timesheetRefs
  }

  fetchAllForTable = () => {
    const result= this.timesheetRefs.map(timesheetRef => {
      return this.convertTimeentryForTable(timesheetRef)
    })
    return result
  }

  fetchAllForDriver = (driverId) => {
    const result= this.timesheetRefs.filter(timesheetRef => {
      return timesheetRef.data().driverId === driverId
    }).map(timesheetRef => {
      return this.convertTimeentryForTable(timesheetRef)
    })
    return result
  }

  fetchAllForDriverForDate = (driverId, date) => {
    // Convert input date to YYYY-MM-DD format for comparison
    const targetDate = new Date(date);
    const targetDateString = targetDate.getFullYear() + '-' + 
      String(targetDate.getMonth() + 1).padStart(2, '0') + '-' + 
      String(targetDate.getDate()).padStart(2, '0');
    
    const result= this.timesheetRefs.filter(timesheetRef => {
      const timesheetData = timesheetRef.data();
      if (timesheetData.driverId !== driverId) return false;
      
      // Convert timesheet date to YYYY-MM-DD format for comparison
      const timesheetDate = new Date(convertFirestoreTimestampToDate(this.firebase, timesheetData.timeIn))
      const timesheetDateString = timesheetDate.getFullYear() + '-' + 
        String(timesheetDate.getMonth() + 1).padStart(2, '0') + '-' + 
        String(timesheetDate.getDate()).padStart(2, '0');
      return timesheetDateString === targetDateString;
    }).map(timesheetRef => {
      return this.convertTimeentryForTable(timesheetRef)
    })
    return result
  }

  calculateDurationForEntry = (timeIn, timeOut) => {
    if (!timeIn || !timeOut) {
      return 0.0;
    }

    let timeInTimestamp, timeOutTimestamp;

    // Handle both Date objects and Firestore timestamps
    if (timeIn instanceof Date) {
      timeInTimestamp = { seconds: Math.floor(timeIn.getTime() / 1000) };
    } else if (timeIn.seconds !== undefined) {
      timeInTimestamp = timeIn;
    } else {
      // Assume it's a timestamp in milliseconds
      timeInTimestamp = { seconds: Math.floor(timeIn / 1000) };
    }

    if (timeOut instanceof Date) {
      timeOutTimestamp = { seconds: Math.floor(timeOut.getTime() / 1000) };
    } else if (timeOut.seconds !== undefined) {
      timeOutTimestamp = timeOut;
    } else {
      // Assume it's a timestamp in milliseconds
      timeOutTimestamp = { seconds: Math.floor(timeOut / 1000) };
    }

    const durationInSeconds = timeOutTimestamp.seconds - timeInTimestamp.seconds;
    const durationInMinutes = durationInSeconds / 60; // Convert to minutes
    const hours = Math.floor(durationInMinutes / 60);
    const minutes = durationInMinutes % 60;
    const durationInHours = hours + (minutes / 60); // Convert minutes to decimal hours
    return parseFloat(durationInHours.toFixed(2)); // Round to 2 decimal places
  }

  add = async (data) => {
    if (data.ref) {
      throw new Error(TimesheetsUtilitiesError.removeRefForUpdate)
    }

    if (!data.timeIn) {
      throw new Error(TimesheetsUtilitiesError.dateIsRequired)
    }
    if (data.timeIn instanceof Date) {
      data.timeIn = convertDateWithTimeToFirestoreTimestamp(this.firebase, data.timeIn)
    } else {
      data.timeIn = convertDateStringToFirestoreTimestamp(this.firebase, new Date(data.timeIn))
    }

    if (data.timeOut) {
      if (data.timeOut instanceof Date) {
        data.timeOut = convertDateWithTimeToFirestoreTimestamp(this.firebase, data.timeOut)
      } else {
        data.timeOut = convertDateStringToFirestoreTimestamp(this.firebase, new Date(data.timeOut))
      }
    }

    // Calculate duration
    data.duration = this.calculateDurationForEntry(data.timeIn, data.timeOut)

    await this.db.collection('time_entries').add(data)
  }


  update = async (id, data) => {
    if (data.ref) {
      throw new Error(TimesheetsUtilitiesError.removeRefForUpdate)
    }

    if (!data.timeIn) {
      throw new Error(TimesheetsUtilitiesError.dateIsRequired)
    }
    if (data.timeIn instanceof Date) {
      data.timeIn = convertDateWithTimeToFirestoreTimestamp(this.firebase, data.timeIn)
    } else {
      data.timeIn = convertDateStringToFirestoreTimestamp(this.firebase, new Date(data.timeIn))
    }

    if (data.timeOut) {
      if (data.timeOut instanceof Date) {
        data.timeOut = convertDateWithTimeToFirestoreTimestamp(this.firebase, data.timeOut)
      } else {
        data.timeOut = convertDateStringToFirestoreTimestamp(this.firebase, new Date(data.timeOut))
      }
    }

    // Calculate duration
    data.duration = this.calculateDurationForEntry(data.timeIn, data.timeOut)

    await this.db.collection('time_entries').doc(id).set(data).catch(error => {
      console.error("Error updating document: ", error);
    });
  }

}